import React, { useState } from 'react';
import { 
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { Download, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { formatCurrency } from '../../utils/currency';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://check.policypulsehq.com/api';

export function Reports() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportType, setReportType] = useState('sales');
  const [groupBy, setGroupBy] = useState('day');
  const [loading, setLoading] = useState(false);
  
  // Report Data States
  const [salesData, setSalesData] = useState(null);
  const [skuData, setSkuData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);

  const formatMoney = (value) => {
    const num = parseFloat(value) || 0;
    return num.toFixed(2);
  };

  const formatNumber = (value) => {
    return parseInt(value) || 0;
  };

  // Generate Reports
  const generateReport = async () => {
    if (reportType !== 'inventory' && (!startDate || !endDate)) {
      toast.error('Please select date range');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      switch (reportType) {
        case 'sales':
        case 'profit':
          const salesRes = await axios.get(`${API_BASE_URL}/shopify-reports/sales`, {
            params: { start_date: startDate, end_date: endDate, group_by: groupBy },
            ...config
          });
          
          console.log('Sales API Response:', salesRes.data);
          
          if (salesRes.data && salesRes.data.success) {
            setSalesData(salesRes.data);
            toast.success(`${reportType === 'sales' ? 'Sales' : 'Profit'} report generated!`);
          } else {
            toast.error('No data returned from server');
          }
          break;

        case 'sku':
          const skuRes = await axios.get(`${API_BASE_URL}/shopify-reports/sku-performance`, {
            params: { start_date: startDate, end_date: endDate, top_n: 100 },
            ...config
          });
          setSkuData(skuRes.data.data);
          toast.success('SKU performance report generated!');
          break;

        case 'inventory':
          const invRes = await axios.get(`${API_BASE_URL}/shopify-reports/inventory`, config);
          setInventoryData(invRes.data.data);
          toast.success('Inventory report generated!');
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('Report Generation Error:', error);
      toast.error(error.response?.data?.error || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // Export current report to CSV
  const exportCurrentReport = async () => {
    if (reportType !== 'inventory' && (!startDate || !endDate)) {
      toast.error('Please select date range for export');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let csvType = reportType;
      
      if (reportType === 'sales' || reportType === 'profit') {
        csvType = 'orders';
      }

      const response = await axios.get(`${API_BASE_URL}/shopify-reports/export/csv`, {
        params: { 
          report_type: csvType,
          start_date: startDate, 
          end_date: endDate 
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      let filename;
      if (reportType === 'inventory') {
        filename = `inventory-report-${new Date().toISOString().split('T')[0]}.csv`;
      } else if (reportType === 'sku') {
        filename = `sku-performance-${startDate}-to-${endDate}.csv`;
      } else {
        filename = `${reportType}-report-${startDate}-to-${endDate}.csv`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('CSV Export Error:', error);
      toast.error('Export failed');
    }
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Reports & Analytics</h1>

      {/* Control Panel */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Report Configuration</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-gray-700 mb-2 font-medium">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value);
                setSalesData(null);
                setSkuData(null);
                setInventoryData(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="sales">Sales Report</option>
              <option value="profit">Profit Analysis</option>
              {/* <option value="sku">SKU Performance</option> */}
              <option value="inventory">Inventory Report</option>
            </select>
          </div>
          
          <div>
            <label className="block text-gray-700 mb-2 font-medium">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={reportType === 'inventory'}
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={reportType === 'inventory'}
            />
          </div>

          {(reportType === 'sales' || reportType === 'profit') && (
            <div>
              <label className="block text-gray-700 mb-2 font-medium">Group By</label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </select>
            </div>
          )}

          <div className="flex items-end gap-2">
            <button
              onClick={generateReport}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 font-medium"
            >
              {loading ? 'Generating...' : 'Generate'}
            </button>
            <button
              onClick={exportCurrentReport}
              disabled={!salesData && !skuData && !inventoryData}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Generate report first, then export"
            >
              <Download size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Sales/Profit Report Summary */}
      {(reportType === 'sales' || reportType === 'profit') && salesData?.summary && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Total Orders</p>
              <p className="text-3xl font-bold text-blue-600">{formatNumber(salesData.summary.total_orders)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Total Revenue</p>
              <p className="text-3xl font-bold text-green-600">
                {formatMoney(salesData.summary.total_revenue)}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Total Profit</p>
              <p className="text-3xl font-bold text-purple-600">
                {formatMoney(salesData.summary.total_profit)}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Profit Margin</p>
              <p className="text-3xl font-bold text-orange-600">
                {salesData.summary.total_revenue > 0 
                  ? ((salesData.summary.total_profit / salesData.summary.total_revenue) * 100).toFixed(1)
                  : '0.0'}%
              </p>
            </div>
          </div>

          {/* Charts for Sales/Profit */}
          {salesData.data && salesData.data.length > 0 ? (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {reportType === 'sales' ? 'Sales Trend Analysis' : 'Profit Analysis Over Time'}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {reportType === 'sales' ? 'Revenue Trend' : 'Revenue & Profit Trend'}
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={salesData.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(value) => `$${formatMoney(value)}`} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#3b82f6" 
                        strokeWidth={2} 
                        name="Revenue"
                        dot={{ r: 4 }}
                      />
                      {reportType === 'profit' && (
                        <Line 
                          type="monotone" 
                          dataKey="profit" 
                          stroke="#10b981" 
                          strokeWidth={2} 
                          name="Profit"
                          dot={{ r: 4 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {reportType === 'sales' ? 'Order Volume' : 'Cost Breakdown'}
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={salesData.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(value) => reportType === 'sales' ? value : `$${formatMoney(value)}`} />
                      <Legend />
                      {reportType === 'sales' ? (
                        <Bar dataKey="orders" fill="#3b82f6" name="Orders" />
                      ) : (
                        <>
                          <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                          <Bar dataKey="cost" fill="#ef4444" name="Cost" />
                          <Bar dataKey="fees" fill="#f59e0b" name="Fees" />
                        </>
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Data Table */}
              <div className="bg-white rounded-xl shadow-lg mt-6 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="text-left py-4 px-6 text-gray-600 font-semibold">Period</th>
                        <th className="text-right py-4 px-6 text-gray-600 font-semibold">Orders</th>
                        <th className="text-right py-4 px-6 text-gray-600 font-semibold">Units</th>
                        <th className="text-right py-4 px-6 text-gray-600 font-semibold">Revenue</th>
                        {reportType === 'profit' && (
                          <>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Cost</th>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Fees</th>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Profit</th>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Margin</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {salesData.data.map((row, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-6 font-medium text-gray-800">{row.period}</td>
                          <td className="py-4 px-6 text-right">{formatNumber(row.orders)}</td>
                          <td className="py-4 px-6 text-right">{formatNumber(row.units_sold)}</td>
                          <td className="py-4 px-6 text-right font-semibold text-blue-600">
                            {formatMoney(row.revenue)}
                          </td>
                          {reportType === 'profit' && (
                            <>
                              <td className="py-4 px-6 text-right text-red-600">
                                {formatMoney(row.cost)}
                              </td>
                              <td className="py-4 px-6 text-right text-orange-600">
                                {formatMoney(row.fees)}
                              </td>
                              <td className="py-4 px-6 text-right font-bold text-green-600">
                                {formatMoney(row.profit)}
                              </td>
                              <td className="py-4 px-6 text-right font-semibold text-purple-600">
                                {row.revenue > 0 ? ((row.profit / row.revenue) * 100).toFixed(1) : '0.0'}%
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : salesData && (!salesData.data || salesData.data.length === 0) ? (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
              <div className="flex items-center">
                <TrendingUp className="text-yellow-600 mr-3" size={24} />
                <div>
                  <h3 className="text-yellow-800 font-semibold">No Data Found</h3>
                  <p className="text-yellow-700">No orders found for the selected date range. Try a different date range or sync your orders first.</p>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}

      {/* SKU Performance Table */}
      {reportType === 'sku' && skuData && skuData.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">SKU Performance Analysis</h2>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">SKU</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-semibold">Product</th>
                    <th className="text-right py-4 px-6 text-gray-600 font-semibold">Orders</th>
                    <th className="text-right py-4 px-6 text-gray-600 font-semibold">Units Sold</th>
                    <th className="text-right py-4 px-6 text-gray-600 font-semibold">Revenue</th>
                    <th className="text-right py-4 px-6 text-gray-600 font-semibold">Profit</th>
                    <th className="text-right py-4 px-6 text-gray-600 font-semibold">Margin</th>
                    <th className="text-right py-4 px-6 text-gray-600 font-semibold">ROI</th>
                    <th className="text-right py-4 px-6 text-gray-600 font-semibold">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {skuData.map((row, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6 font-medium text-gray-800">{row.sku || 'N/A'}</td>
                      <td className="py-4 px-6 text-gray-700">{row.product_name}</td>
                      <td className="py-4 px-6 text-right">{formatNumber(row.order_count)}</td>
                      <td className="py-4 px-6 text-right">{formatNumber(row.total_units_sold)}</td>
                      <td className="py-4 px-6 text-right font-semibold text-blue-600">
                        {formatMoney(row.total_revenue)}
                      </td>
                      <td className="py-4 px-6 text-right font-semibold text-green-600">
                        {formatMoney(row.total_profit)}
                      </td>
                      <td className="py-4 px-6 text-right font-semibold text-purple-600">
                        {row.profit_margin}%
                      </td>
                      <td className="py-4 px-6 text-right font-semibold text-orange-600">
                        {row.roi}%
                      </td>
                      <td className="py-4 px-6 text-right">
                        <span className={`px-2 py-1 rounded text-sm ${
                          row.current_inventory === 0 ? 'bg-red-100 text-red-700' :
                          row.current_inventory <= 10 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {formatNumber(row.current_inventory)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Report */}
      {reportType === 'inventory' && inventoryData && inventoryData.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Current Inventory Status</h2>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="text-left py-4 px-6 text-gray-600 font-semibold">SKU</th>
                  <th className="text-left py-4 px-6 text-gray-600 font-semibold">Product</th>
                  <th className="text-right py-4 px-6 text-gray-600 font-semibold">Total Stock</th>
                  <th className="text-right py-4 px-6 text-gray-600 font-semibold">Stock Value</th>
                  <th className="text-left py-4 px-6 text-gray-600 font-semibold">Variants</th>
                </tr>
              </thead>
              <tbody>
                {inventoryData.map((row, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-6 font-medium text-gray-800">{row.sku || 'N/A'}</td>
                    <td className="py-4 px-6 text-gray-700">{row.product_name}</td>
                    <td className="py-4 px-6 text-right">
                      <span className={`px-3 py-1 rounded font-semibold ${
                        row.total_inventory === 0 ? 'bg-red-100 text-red-700' :
                        row.total_inventory <= 10 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {row.total_inventory}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right font-semibold text-blue-600">
                      {formatMoney(row.total_value)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex flex-wrap gap-1">
                        {row.variants.map((v, i) => (
                          <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            {v.variant_title || 'Default'}: {v.inventory}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reports;